/**
 * 格式化日期
 * @param {Date|string|number} date 
 * @param {string} format 
 * @returns {string}
 */
export function formatDate(date, format = 'YYYY-MM-DD HH:mm:ss') {
  if (!date) return ''
  
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const seconds = String(d.getSeconds()).padStart(2, '0')
  
  return format
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds)
}

/**
 * 防抖函数
 * @param {Function} func 
 * @param {number} delay 
 * @returns {Function}
 */
export function debounce(func, delay = 300) {
  let timeoutId
  return function (...args) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(this, args), delay)
  }
}

/**
 * 节流函数
 * @param {Function} func 
 * @param {number} delay 
 * @returns {Function}
 */
export function throttle(func, delay = 300) {
  let lastTime = 0
  return function (...args) {
    const now = Date.now()
    if (now - lastTime >= delay) {
      func.apply(this, args)
      lastTime = now
    }
  }
}

/**
 * 深拷贝
 * @param {*} obj 
 * @returns {*}
 */
export function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Date) return new Date(obj)
  if (obj instanceof Array) return obj.map(item => deepClone(item))
  if (typeof obj === 'object') {
    const clonedObj = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key])
      }
    }
    return clonedObj
  }
}

/**
 * 生成随机字符串
 * @param {number} length 
 * @returns {string}
 */
export function generateRandomString(length = 8) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

/**
 * 验证手机号
 * @param {string} phone 
 * @returns {boolean}
 */
export function validatePhone(phone) {
  return /^1[3-9]\d{9}$/.test(phone)
}

/**
 * 验证邮箱
 * @param {string} email 
 * @returns {boolean}
 */
export function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

/**
 * 验证身份证号
 * @param {string} idCard 
 * @returns {boolean}
 */
export function validateIdCard(idCard) {
  return /^[1-9]\d{5}(18|19|20)\d{2}((0[1-9])|(1[0-2]))(([0-2][1-9])|10|20|30|31)\d{3}[0-9Xx]$/.test(idCard)
}

/**
 * 格式化文件大小
 * @param {number} bytes 
 * @returns {string}
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * 获取文件扩展名
 * @param {string} filename 
 * @returns {string}
 */
export function getFileExtension(filename) {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2)
}

/**
 * 滚动到顶部
 * @param {number} duration 
 */
export function scrollToTop(duration = 300) {
  const start = window.pageYOffset
  const startTime = performance.now()
  
  function scroll() {
    const now = performance.now()
    const time = Math.min(1, (now - startTime) / duration)
    const timeFunction = time * (2 - time) // easeInOutQuad
    window.scroll(0, Math.ceil(timeFunction * (0 - start) + start))
    
    if (window.pageYOffset === 0) return
    requestAnimationFrame(scroll)
  }
  
  scroll()
}

/**
 * 获取URL参数
 * @param {string} name 
 * @returns {string|null}
 */
export function getUrlParam(name) {
  const reg = new RegExp('(^|&)' + name + '=([^&]*)(&|$)')
  const r = window.location.search.substr(1).match(reg)
  if (r != null) return decodeURIComponent(r[2])
  return null
}

/**
 * 设置页面标题
 * @param {string} title 
 */
export function setPageTitle(title) {
  document.title = title ? `${title} - 应急科技推介平台` : '应急科技推介平台'
}

/**
 * 处理图片URL
 * @param {string} imageUrl 
 * @returns {string}
 */
// 图片URL处理缓存（避免重复处理相同URL）
const imageUrlCache = new Map()
const MAX_CACHE_SIZE = 200 // 最多缓存200个URL

export function processImageUrl(imageUrl) {
  if (!imageUrl) return ''
  
  // 检查缓存
  if (imageUrlCache.has(imageUrl)) {
    return imageUrlCache.get(imageUrl)
  }
  
  // 生产环境的基础URL
  const baseUrl = process.env.VUE_APP_API_BASE_URL || 'https://www.zgyjkj.com'
  
  // 如果是完整的URL
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    // 检查是否是localhost开发地址，需要替换为生产域名
    if (imageUrl.includes('localhost') || imageUrl.includes('127.0.0.1')) {
      try {
        // 提取路径部分（/uploads/...）
        const urlObj = new URL(imageUrl)
        const path = urlObj.pathname
        
        // 生产环境使用完整URL
        if (process.env.NODE_ENV === 'production') {
          const result = `${baseUrl}${path}`
          _cacheResult(imageUrl, result)
          return result
        }
        
        // 开发环境使用相对路径
        _cacheResult(imageUrl, path)
        return path
      } catch (e) {
        console.warn('解析localhost URL失败:', imageUrl, e)
        // 尝试提取路径部分
        const match = imageUrl.match(/\/uploads\/.*/)
        if (match) {
          const result = match[0]
          _cacheResult(imageUrl, result)
          return result
        }
        _cacheResult(imageUrl, imageUrl)
        return imageUrl
      }
    }
    
    // 已经是正确的生产URL，直接返回
    _cacheResult(imageUrl, imageUrl)
    return imageUrl
  }
  
  // 开发环境使用相对路径，让代理处理
  if (process.env.NODE_ENV === 'development') {
    // 如果已经包含uploads路径，直接返回
    if (imageUrl.includes('uploads/')) {
      // 确保以/开头
      const result = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`
      _cacheResult(imageUrl, result)
      return result
    }
    
    // 如果是相对路径（以/开头），直接返回
    if (imageUrl.startsWith('/')) {
      _cacheResult(imageUrl, imageUrl)
      return imageUrl
    }
    
    // 如果是文件名，添加uploads路径
    const result = `/uploads/${imageUrl}`
    _cacheResult(imageUrl, result)
    return result
  }
  
  // 生产环境使用完整URL
  // 如果已经包含uploads路径，添加基础URL
  if (imageUrl.includes('uploads/')) {
    // 确保以/开头
    const path = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`
    const result = `${baseUrl}${path}`
    _cacheResult(imageUrl, result)
    return result
  }
  
  // 如果是相对路径（以/开头），添加基础URL
  if (imageUrl.startsWith('/')) {
    const result = `${baseUrl}${imageUrl}`
    _cacheResult(imageUrl, result)
    return result
  }
  
  // 如果是文件名，添加uploads路径和基础URL
  const result = `${baseUrl}/uploads/${imageUrl}`
  _cacheResult(imageUrl, result)
  return result
}

// 缓存结果的辅助函数
function _cacheResult(imageUrl, result) {
  // 缓存结果（限制缓存大小）
  if (imageUrlCache.size >= MAX_CACHE_SIZE) {
    // 清除最旧的缓存（FIFO策略）
    const firstKey = imageUrlCache.keys().next().value
    imageUrlCache.delete(firstKey)
  }
  imageUrlCache.set(imageUrl, result)
}

/**
 * 去除HTML标签，返回纯文本
 * @param {string} html 
 * @returns {string}
 */
export function stripHtml(html) {
  if (!html) return ''
  // 创建临时div元素
  const temp = document.createElement('div')
  temp.innerHTML = html
  // 获取纯文本内容
  return temp.textContent || temp.innerText || ''
}

/**
 * 安全访问 localStorage，避免浏览器「跟踪防护」阻断时抛错
 * @param {string} key
 * @returns {string|null}
 */
export function safeLocalStorageGet(key) {
  try {
    return localStorage.getItem(key)
  } catch (_) {
    return null
  }
}

/**
 * 安全写入 localStorage
 * @param {string} key
 * @param {string} value
 */
export function safeLocalStorageSet(key, value) {
  try {
    localStorage.setItem(key, value)
  } catch (_) {}
}

/**
 * 安全删除 localStorage
 * @param {string} key
 */
export function safeLocalStorageRemove(key) {
  try {
    localStorage.removeItem(key)
  } catch (_) {}
} 